import { AppBar, Container, IconButton, SvgIcon, Toolbar, Typography } from '@material-ui/core';
import React, { useCallback, useState } from 'react';
import { useTheme } from '@material-ui/core/styles';

import { ReactComponent as MenuIcon } from 'assets/icons/menu-icon.svg';
import { useStyles } from 'features/app/components/AppHeader/styles';
import { BalanceDropdown } from 'features/app/components/BalanceDropdown';
import { HeaderUserProfile } from 'features/app/components/HeaderUserProfile';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useTranslation } from 'hooks/useTranslation';
import { dashboardPaths } from 'features/dashboard/routes/paths';
import { myGiftCardsPaths } from 'features/my-gift-cards/routes/paths';
import { orderHistoryPaths } from 'features/order-history/routes/paths';
import { usersPaths } from 'features/users/routes/paths';
import { redemptionsPaths } from 'features/redemptions/routes/paths';
import { tutorialsPaths } from 'features/tutorials/routes/paths';
import { financePaths } from 'features/finance/routes/paths';
import { getStartedPaths } from 'features/get-started/routes/paths';
import { RegistrationRequestStatus } from 'features/auth/components/RegistrationRequestStatus';
import useUser from 'features/user/hooks/useUser';
import { RegistrationRequestStatusType } from 'features/auth/ts';
import useAuth from 'features/auth/hooks/useAuth';
import i18n from 'locales/i18n';
import { subscriptionPaths } from 'features/subscription/routes/paths';
import { SubscriptionHeader } from 'features/subscription/components/StatusHeader';
import { useSubscription } from 'features/user/hooks/useSubscription';

interface AppHeaderProps {
  setSidebarShown: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AppHeader: React.FC<AppHeaderProps> = ({ setSidebarShown }) => {
  const location = useLocation();
  const direction = i18n.dir();
  const classes = useStyles({ direction });
  const theme = useTheme();
  const { t } = useTranslation('app');
  const [showRegistrationStatus, setShowRegistrationStatus] = useState(false);
  const { user } = useUser();
  const { isSubscriptionActive } = useSubscription();
  const { isFirstLogin, isUploadDocsVerificationShown, onShowUploadVerificationDocumentsModal } = useAuth();

  const checkShowRegistrationStatus = useCallback(() => {
    setShowRegistrationStatus(location.pathname === dashboardPaths.static.root && !isUploadDocsVerificationShown);
  }, [isUploadDocsVerificationShown, location.pathname]);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (user?.registration_request_status === RegistrationRequestStatusType.APPROVED) {
      if (isFirstLogin) {
        checkShowRegistrationStatus();
        timeout = setTimeout(() => {
          setShowRegistrationStatus(false);
        }, 5000);
      }
    } else {
      checkShowRegistrationStatus();
    }
    return () => clearTimeout(timeout);
  }, [isFirstLogin, checkShowRegistrationStatus, showRegistrationStatus, user?.registration_request_status]);

  return (
    <AppBar className={classes.root}>
      <Toolbar className={classes.toolbar}>
        <IconButton onClick={() => setSidebarShown(v => !v)} className={classes.menuIcon}>
          <SvgIcon component={MenuIcon} htmlColor={theme.palette.common.black} />
        </IconButton>
        <Container className={classes.headerItems}>
          <Typography variant="h3" className={classes.title}>
            <Switch>
              <Route path={dashboardPaths.static.root}>{t('dashboard')}</Route>
              <Route path={subscriptionPaths.static.root}>{t('subscription')}</Route>
              <Route path={myGiftCardsPaths.static.root}>{t('myRewards')}</Route>
              <Route path={orderHistoryPaths.static.root}>{t('orderHistory')}</Route>
              <Route path={usersPaths.static.root}>{t('users')}</Route>
              <Route path={redemptionsPaths.static.root}>{t('redemptions')}</Route>
              <Route path={tutorialsPaths.static.root}>{t('tutorials')}</Route>
              <Route path={financePaths.static.balanceAndTopUp}>{t('balanceAndTopUp')}</Route>
              <Route path={getStartedPaths.static.root}>{t('getStarted')}</Route>
            </Switch>
          </Typography>
          <BalanceDropdown />
          <HeaderUserProfile />
        </Container>
      </Toolbar>
      {showRegistrationStatus && user ? (
        <RegistrationRequestStatus
          onShowUploadVerificationDocumentsModal={onShowUploadVerificationDocumentsModal}
          user={user}
        />
      ) : (
        !isSubscriptionActive &&
        user?.registration_request_status === RegistrationRequestStatusType.APPROVED && <SubscriptionHeader />
      )}
    </AppBar>
  );
};
